import { observer } from 'mobx-react';
import { Colors, Text } from '@doordash/design-language';

// Hooks
import { useStores } from 'hooks/use-stores';

import Tag, { TagIconType, TagSize, TagType } from '@doordash/component-tag';

// Components
import { CarouselIcon } from 'components/cart-item-editor/recommendationStyles';
import EmptyIcon from 'components/cart-item-editor/EmptyIcon';
import { PrismTagWrapperPositiveDefault } from 'components/PrismWrappers';

// Utils
import { formatCentsToPrice } from 'utils/Currency';
import combineURLs from 'axios/lib/helpers/combineURLs';
import { getCustomBogoText } from '../../../stores/DvUtils';

// Styles
import {
  ItemLabelText,
  ListItemDescription,
  ListItemWrapper,
  ModifiersGroup,
  PriceContainer,
  PriceText,
} from './styles';

// Types
import { ListItemType } from './types';

function ListItem({
  item,
  priceAfterBOGO,
  children,
}: {
  item: ListItemType;
  priceAfterBOGO?: number;
  children: React.ReactNode;
}) {
  const { locationStore } = useStores();
  const { name, mods, price, originalPrice, imageUrl = '' } = item;
  const imgSrc = imageUrl?.includes('http')
    ? imageUrl
    : combineURLs(process.env.REACT_APP_STATIC_ROOT ?? '', imageUrl ?? '');
  const customBogoText = getCustomBogoText(locationStore.customer.customer_id);

  return (
    <ListItemWrapper>
      {imageUrl ? <CarouselIcon fromCart src={imgSrc} alt={name} /> : <EmptyIcon fromCart />}
      <ListItemDescription>
        <ItemLabelText data-testid="cart-item" color={Colors.SystemGrey95} styles={Text.Styles.Bold14}>
          {name}
        </ItemLabelText>
        <ModifiersGroup>
          {mods.map(({ qty, name: modName }) => `${qty > 1 ? `${qty}x ` : ''}${modName}`).join(', ')}
        </ModifiersGroup>
        <Price
          pricePaid={price}
          originalPrice={originalPrice ?? price}
          currencyCode={locationStore.currencyCode}
          priceAfterBOGO={priceAfterBOGO}
        />
        {priceAfterBOGO !== undefined && (
          <PrismTagWrapperPositiveDefault>
            <Tag
              type={TagType.PositiveDefault}
              text={customBogoText || 'Buy 1, Get 1 Free'}
              size={TagSize.Small}
              leadingIconType={TagIconType.PromoFill}
              hasLeadingIcon
            />
          </PrismTagWrapperPositiveDefault>
        )}
      </ListItemDescription>
      {children}
    </ListItemWrapper>
  );
}

function Price({
  pricePaid,
  originalPrice,
  currencyCode,
  priceAfterBOGO,
}: {
  pricePaid: number;
  originalPrice: number;
  currencyCode: string;
  priceAfterBOGO?: number;
}) {
  const hasBeenRefunded = originalPrice > pricePaid;
  return (
    <PriceContainer>
      <PriceText hasBeenRefunded={hasBeenRefunded} hasBOGO={priceAfterBOGO !== undefined}>
        {formatCentsToPrice(originalPrice, currencyCode)}
      </PriceText>
      {priceAfterBOGO !== undefined && <PriceText>{formatCentsToPrice(priceAfterBOGO, currencyCode)}</PriceText>}
      {hasBeenRefunded && <PriceText>{formatCentsToPrice(pricePaid, currencyCode)}</PriceText>}
    </PriceContainer>
  );
}

export default observer(ListItem);
